import React from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  Dialog as DialogComponent,
  Text,
  TextPriority,
  TextTypography as TYPOGRAPHY,
} from 'wix-ui-tpa/cssVars';
import { DataHooks } from './constants';
import { classes, st, vars } from './ServiceDetailsDialog.st.css';
import { ServiceDetailsViewModel } from '../../../components/DailyAgenda/viewModel/serviceDetailsViewModel/serviceDetailsViewModel';
import { useDailyAgendaActions } from '../../../components/DailyAgenda/hooks/useDailyAgendaActions';
import ServiceDetailsHero from './components/ServiceDetailsHero/ServiceDetailsHero';
import ServiceDetailsGallery from './components/ServiceDetailsGallery/ServiceDetailsGallery';

export type ServiceDetailsDialogProps = {
  viewModel: ServiceDetailsViewModel;
};

const TextSection: React.FC<{
  title: string;
  text: string;
  sectionDataHook?: string;
  titleDataHook: string;
  textDataHook: string;
}> = ({ title, text, sectionDataHook, titleDataHook, textDataHook }) => (
  <div className={classes.section} data-hook={sectionDataHook}>
    <Text
      className={classes.sectionTitle}
      data-hook={titleDataHook}
      typography={TYPOGRAPHY.smallTitle}
      tagName="h3"
    >
      {title}
    </Text>
    <Text
      className={classes.sectionText}
      data-hook={textDataHook}
      typography={TYPOGRAPHY.runningText}
      tagName="p"
    >
      {text}
    </Text>
  </div>
);

const ServiceDetailsDialog: React.FC<ServiceDetailsDialogProps> = ({
  viewModel,
}) => {
  const { onServiceDetailsClose } = useDailyAgendaActions();
  const { isMobile } = useEnvironment();
  const { t } = useTranslation();
  const {
    isOpen,
    closeButtonAriaLabel,
    serviceName,
    tagLine,
    description,
    serviceImage,
    galleryItems,
    availableOnline,
    duration,
    price,
    priceAfterDiscount,
    discountName,
    showCalculatedAtCheckout,
    locations,
    policy,
    contactInfo,
    course,
  } = viewModel;

  // The small screen hides the course status on the row (Figma 4235-117673),
  // so the dialog must show the box even when nothing else fills it.
  const hasDetails = !!(duration || price || locations?.length || course);

  return (
    <DialogComponent
      data-hook={DataHooks.Root}
      isOpen={isOpen}
      onClose={() => onServiceDetailsClose()}
      closeButtonAriaLabel={closeButtonAriaLabel}
      className={st(classes.root, { isMobile })}
      notFullscreenOnMobile
    >
      <ServiceDetailsHero
        serviceImage={serviceImage}
        serviceName={serviceName}
        availableOnline={availableOnline}
      />
      {serviceName && (
        <Text
          className={classes.title}
          data-hook={DataHooks.Title}
          typography={TYPOGRAPHY.largeTitle}
          tagName="h2"
        >
          {serviceName}
        </Text>
      )}
      {tagLine && (
        <Text
          className={classes.tagLine}
          data-hook={DataHooks.TagLine}
          typography={TYPOGRAPHY.runningText}
          tagName="p"
        >
          {tagLine}
        </Text>
      )}
      {hasDetails && (
        <div className={classes.detailsBox} data-hook={DataHooks.DetailsList}>
          {duration && (
            <Text
              className={classes.box}
              data-hook={DataHooks.Duration}
              tagName="span"
            >
              {duration}
            </Text>
          )}
          {price &&
            (priceAfterDiscount ? (
              <span className={st(classes.box, classes.pricesRow)}>
                <Text
                  className={classes.priceBeforeDiscount}
                  data-hook={DataHooks.PriceBeforeDiscount}
                >
                  {price}
                </Text>
                <Text data-hook={DataHooks.PriceAfterDiscount}>
                  {priceAfterDiscount}
                </Text>
              </span>
            ) : (
              <Text
                className={classes.box}
                data-hook={DataHooks.Price}
                tagName="span"
              >
                {price}
              </Text>
            ))}
          {course?.dateRange && (
            <Text
              className={classes.box}
              data-hook={DataHooks.CourseDateRange}
              tagName="span"
            >
              {course.dateRange}
            </Text>
          )}
          {course?.statusText && (
            <Text
              className={classes.box}
              data-hook={DataHooks.CourseStatus}
              tagName="span"
            >
              {course.statusText}
            </Text>
          )}
          {locations?.length ? (
            <Text
              className={classes.box}
              data-hook={DataHooks.Location}
              tagName="span"
            >
              {locations.map((location, index) => (
                <React.Fragment key={location}>
                  {location}
                  {index !== locations.length - 1 && (
                    <span className={classes.locationDivider}>|</span>
                  )}
                </React.Fragment>
              ))}
            </Text>
          ) : null}
        </div>
      )}
      {discountName && (
        <div
          className={classes.discountName}
          data-hook={DataHooks.DiscountName}
        >
          <Text className={classes.discountNameText}>{discountName}</Text>
          {showCalculatedAtCheckout && (
            <Text
              className={classes.calculatedAtCheckout}
              data-hook={DataHooks.CalculatedAtCheckout}
              priority={TextPriority.secondary}
            >
              {t('app.discount-calculated-at-checkout')}
            </Text>
          )}
        </div>
      )}
      {description && (
        <TextSection
          title={t('service-details.description.title')}
          text={description}
          titleDataHook={DataHooks.DescriptionTitle}
          textDataHook={DataHooks.Description}
        />
      )}
      {galleryItems?.length ? (
        <ServiceDetailsGallery items={galleryItems} />
      ) : null}
      {policy && (
        <TextSection
          title={t('service-details.policy.title')}
          text={policy}
          sectionDataHook={DataHooks.PolicySection}
          titleDataHook={DataHooks.PolicyTitle}
          textDataHook={DataHooks.Policy}
        />
      )}
      {contactInfo?.length ? (
        <div className={classes.section} data-hook={DataHooks.ContactSection}>
          <Text
            className={classes.sectionTitle}
            data-hook={DataHooks.ContactTitle}
            typography={TYPOGRAPHY.smallTitle}
            tagName="h3"
          >
            {t('service-details.contact.title')}
          </Text>
          <ul
            className={classes.contactLocations}
            style={{
              [vars.contactMaxColumns]: Math.min(contactInfo.length, 3),
            }}
          >
            {contactInfo.map((contact, index) => (
              <li
                className={classes.contactLocation}
                data-hook={DataHooks.ContactLocation}
                key={contact.address ?? index}
              >
                {contact.address && (
                  <Text
                    className={classes.sectionText}
                    data-hook={DataHooks.ContactAddress}
                    typography={TYPOGRAPHY.runningText}
                    tagName="p"
                  >
                    {contact.address}
                  </Text>
                )}
                {contact.phone && (
                  <Text
                    className={classes.sectionText}
                    data-hook={DataHooks.ContactPhone}
                    typography={TYPOGRAPHY.runningText}
                    tagName="p"
                  >
                    {contact.phone}
                  </Text>
                )}
                {contact.email && (
                  <Text
                    className={classes.sectionText}
                    data-hook={DataHooks.ContactEmail}
                    typography={TYPOGRAPHY.runningText}
                    tagName="p"
                  >
                    {contact.email}
                  </Text>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </DialogComponent>
  );
};

export default ServiceDetailsDialog;
